import {
  AggregationGroupBy,
  WorkpackTypeFilterOption,
  WbsFilterOption,
  useUpdateKpiFiltersInPlatformPreferencesMutation,
} from '@/__generated__/graphql';
import { useRouter } from '@/hooks';
import { useGetPlatformId } from '@/hooks/useGetPlatformId';
import { extractErrorMessageFromUnknownError } from '@/utils/error';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useCallback, useMemo } from 'react';
import { z } from 'zod';
import { kpiFilterSchema } from '../schemas';

type PlatformPreferencesSetPayload = Partial<z.infer<typeof kpiFilterSchema>>;

export const usePersistKpiFilters = () => {
  const router = useRouter();
  const { platformId } = router.query;
  const currentPlatformId = useGetPlatformId();
  const { showSnackBar } = useSnackBarMessage({});

  // Use router platform ID if available, otherwise fall back to hook platform ID
  const effectivePlatformId = typeof platformId === 'string' ? platformId : currentPlatformId;

  const [mutate, { loading }] = useUpdateKpiFiltersInPlatformPreferencesMutation();

  /**
   * Factory function to create persist handlers with common error handling
   */
  const createPersistFn = useCallback(
    <TArgs extends unknown[]>(
      buildPayload: (platformId: string, ...args: TArgs) => PlatformPreferencesSetPayload
    ) => {
      return async (...args: TArgs) => {
        try {
          if (typeof effectivePlatformId !== 'string') {
            throw Error('Cannot determine platform ID');
          }
          const set = buildPayload(effectivePlatformId, ...args);
          await mutate({ variables: { input: { platformId: effectivePlatformId, set } } });
        } catch (error) {
          const message = extractErrorMessageFromUnknownError(error);
          showSnackBar(message, 'error');
        }
      };
    },
    [mutate, effectivePlatformId, showSnackBar]
  );

  const persistFns = useMemo(
    () => ({
      persistSelectedDeploymentsFilter: createPersistFn((_, ids: string[]) => {
        if (!ids?.length) throw Error('At least one ID must be selected');
        return { filters: { kpi_selected_deployments: ids } };
      }),

      persistSelectedDateAggregationFilter: createPersistFn(
        (_, aggregation: AggregationGroupBy) => {
          if (!aggregation) throw Error('Invalid value for aggregation filter');
          return { filters: { kpi_aggregation_filter: aggregation } };
        }
      ),

      persistSelectedDateRangeFilter: createPersistFn(
        (_, startDate: Date | undefined, endDate: Date | undefined) => {
          const formatDate = (date: Date | undefined): string | undefined => {
            if (!date) return undefined;
            return new Intl.DateTimeFormat('en-CA', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            }).format(date);
          };
          const from = formatDate(startDate);
          const to = formatDate(endDate);

          const dateRange = {
            from: from ?? null,
            to: to ?? null,
          };

          return { filters: { kpi_date_range: dateRange } } as PlatformPreferencesSetPayload;
        }
      ),

      persistVisibleColumnKeys: createPersistFn((_, filterKeys: string[]) => ({
        columns: { kpi_table: filterKeys },
      })),

      persistExportColumnKeys: createPersistFn((_, filterKeys: string[]) => ({
        columns: { kpi_export: filterKeys },
      })),

      persistSelectedWorkpackTypesFilter: createPersistFn(
        (_, types: WorkpackTypeFilterOption[]) => ({
          filters: { kpi_selected_workpack_types: types },
        })
      ),

      persistSelectedWbsFilter: createPersistFn((_, wbs: WbsFilterOption | undefined) => ({
        filters: { kpi_selected_wbs: wbs },
      })),
    }),
    [createPersistFn]
  );

  return { loading, ...persistFns };
};
